import { useCurrentWorkspace } from '@gripp/shared-logic';
import {
  AlertsManagementList,
  AlertsManagementTableData,
  Colors,
  ColumnData,
  EditAlertModal,
  SelectionStatus,
  SlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { useMaxHeight } from '../../shared/hooks/useMaxHeight';
import { AlertsManagementFilters } from './alertsManagementFilters';
import { AlertsSelectionPanelMain } from './alertsSelectionPanel';
import { useWorkflow } from './useWorkflow';

export const AlertsManagement = () => {
  return <AlertsManagementBase />;
};

export const AlertsManagementBase = ({
  rightColumn: rightColumnOverride,
  viewMode: viewModeOverride,
}: {
  rightColumn?: ColumnData;
  viewMode?: ViewMode;
} = {}) => {
  const { t, i18n } = useTranslation();
  const { containerMaxHeight, listMaxHeight } = useMaxHeight(64, 0, 228);
  const workspace = useCurrentWorkspace();
  const [alertSummaryId, setAlertSummaryId] = useState<string>('');
  const [editAlertModalVisible, setEditAlertModalVisible] = useState(false);
  const [editAlertModalStyle, setEditAlertModalStyle] = useState<
    StyleProp<ViewStyle>
  >(styles.hidden);
  const { workflow } = useWorkflow(alertSummaryId);

  const [rowsSelected, setRowsSelected] = useState<AlertsManagementTableData[]>(
    []
  );
  const [selectionStatus, setSelectionStatus] =
    useState<SelectionStatus>('idle');

  const rowsSelectedIds = useMemo(
    () => rowsSelected.map((r) => r.id),
    [rowsSelected]
  );
  const hasSelection = rowsSelected.length > 0;

  const clearSelection = useCallback(() => {
    setRowsSelected([]);
    setSelectionStatus('reset');
    rightColumnOverride?.onBackButtonPress?.();
  }, [rightColumnOverride]);

  const onRowClick = useCallback((alert: AlertsManagementTableData) => {
    const alertId = alert?.id;
    if (!alertId) return;
    setAlertSummaryId(alertId);
    handleShowEditAlertModal();
  }, []);

  const handleShowEditAlertModal = useCallback(() => {
    setEditAlertModalVisible(true);
    setEditAlertModalStyle(styles.webModalBackground);
  }, []);

  const handleCloseEditAlertModal = useCallback(() => {
    setEditAlertModalVisible(false);
    setEditAlertModalStyle(styles.hidden);
    setAlertSummaryId('');
  }, []);

  const selectionRightColumn = useMemo<ColumnData | undefined>(() => {
    if (!hasSelection) return undefined;

    const count = rowsSelected.length;
    const title =
      count === 1
        ? t('alertsManagement.topBar.alertSelected', {
            defaultValue: '1 alert selected',
          })
        : t('alertsManagement.topBar.alertsSelected', {
            count,
            defaultValue: `${count} alerts selected`,
          });
    i18n.addResource(i18n.language, 'translation', title, title);

    return {
      title,
      mainComponent: (
        <AlertsSelectionPanelMain
          alerts={rowsSelected}
          onStatusChanged={clearSelection}
        />
      ),
      showBackButton: true,
      onBackButtonPress: clearSelection,
      disableScroll: false,
    };
  }, [hasSelection, rowsSelected, clearSelection, t, i18n]);

  // Selection panel takes priority; fall back to the caller-injected override
  // (used by the /alerts/filters sibling route to show filters in the right
  // column without re-mounting this screen).
  const resolvedRightColumn = selectionRightColumn ?? rightColumnOverride;
  const resolvedViewMode =
    viewModeOverride ??
    (hasSelection || rightColumnOverride ? ViewMode.DUAL : ViewMode.SINGLE);

  if (!workspace) {
    return null;
  }

  const filterComponent = <AlertsManagementFilters />;

  const columnComponent = (
    <View style={styles.tableContainer}>
      <AlertsManagementList
        maxHeight={listMaxHeight}
        onRowClick={onRowClick}
        onRowsSelectedChange={setRowsSelected}
        selectedRowIds={rowsSelectedIds}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={setSelectionStatus}
      />

      {!!alertSummaryId &&
        workflow?.workspaceAsset?.id &&
        workflow.trigger?.config?.workspaceRoutineId && (
          <EditAlertModal
            editAlertModalVisible={editAlertModalVisible}
            onClose={handleCloseEditAlertModal}
            workspaceAssetId={workflow.workspaceAsset.id}
            routineId={workflow.trigger.config.workspaceRoutineId}
            alertId={alertSummaryId}
            editAlertModalStyle={editAlertModalStyle}
          />
        )}
    </View>
  );

  return (
    <SlideLayout
      leftColumn={{
        title: 'alertsManagement.menu.title',
        mainComponent: columnComponent,
        headerComponent: filterComponent,
        disableScroll: true,
      }}
      rightColumn={resolvedRightColumn}
      viewMode={resolvedViewMode}
    />
  );
};

const styles = StyleSheet.create({
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: 44,
    marginBottom: 20,
  },
  searchContainer: {
    width: 390,
    marginRight: 20,
  },
  tableContainer: {
    flex: 1,
    borderRadius: 0,
    overflow: 'hidden',
    zIndex: -1,
  },
  hidden: {
    height: 0,
    width: 0,
    maxHeight: 0,
    minHeight: 0,
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  webModalBackground: {
    flex: 1,
    backgroundColor: Colors.role.backdrop,
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
